import React, { useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  BarGraphIcon1,
  BarGraphIcon2,
  BarGraphIcon3,
  HelpCircleIcon,
} from "../../../commonUI/Icons/SvgIcon";
import TooltipIcon from "../../../commonUI/Tooltip/TooltipIcon";
import KPICardsGrid from "../../../commonUI/Cards/KPICardsGrid";

const INTERVAL_OPTIONS = ["Daily", "Weekly", "Monthly", "Yearly"];

const METRIC_OPTIONS = [
  { value: "users-sessions", label: "Users and sessions" },
  { value: "active-users", label: "Active users" },
  { value: "sessions", label: "Sessions" },
];

const WEBSITE_CHART_DATA = [
  { month: "22 Aug", activeUsers: 500, sessions: 320 },
  { month: "23 Aug", activeUsers: 770, sessions: 380 },
  { month: "24 Aug", activeUsers: 520, sessions: 290 },
  { month: "25 Aug", activeUsers: 480, sessions: 270 },
  { month: "26 Aug", activeUsers: 590, sessions: 350 },
  { month: "27 Aug", activeUsers: 405, sessions: 280 },
  { month: "28 Aug", activeUsers: 910, sessions: 380 },
  { month: "29 Aug", activeUsers: 250, sessions: 180 },
  { month: "30 Aug", activeUsers: 320, sessions: 220 },
  { month: "31 Aug", activeUsers: 500, sessions: 310 },
  { month: "1 Sep", activeUsers: 810, sessions: 350 },
  { month: "2 Sep", activeUsers: 690, sessions: 310 },
  { month: "3 Sep", activeUsers: 590, sessions: 270 },
  { month: "4 Sep", activeUsers: 450, sessions: 260 },
  { month: "5 Sep", activeUsers: 380, sessions: 240 },
  { month: "6 Sep", activeUsers: 500, sessions: 290 },
  { month: "7 Sep", activeUsers: 440, sessions: 260 },
  { month: "8 Sep", activeUsers: 350, sessions: 210 },
  { month: "9 Sep", activeUsers: 300, sessions: 200 },
  { month: "10 Sep", activeUsers: 290, sessions: 180 },
  { month: "11 Sep", activeUsers: 490, sessions: 300 },
  { month: "12 Sep", activeUsers: 750, sessions: 360 },
  { month: "13 Sep", activeUsers: 640, sessions: 310 },
  { month: "14 Sep", activeUsers: 580, sessions: 330 },
  { month: "15 Sep", activeUsers: 600, sessions: 340 },
  { month: "16 Sep", activeUsers: 480, sessions: 280 },
  { month: "17 Sep", activeUsers: 440, sessions: 270 },
  { month: "18 Sep", activeUsers: 460, sessions: 290 },
  { month: "19 Sep", activeUsers: 410, sessions: 260 },
  { month: "20 Sep", activeUsers: 390, sessions: 240 },
];

const TRAFFIC_CHANNELS = [
  { channel: "Organic search", sessions: 8008, shareOfSessions: "42.4%", engagementRate: "80.6%" },
  { channel: "Direct", sessions: 4819, shareOfSessions: "25.5%", engagementRate: "84.9%" },
  { channel: "Referral", sessions: 2414, shareOfSessions: "12.8%", engagementRate: "74.3%" },
  { channel: "Paid search", sessions: 1684, shareOfSessions: "8.9%", engagementRate: "65.6%" },
  { channel: "Organic social", sessions: 1138, shareOfSessions: "6.0%", engagementRate: "58.6%" },
];

const LANDING_PAGES = [
  { page: "/", sessions: 6605 },
  { page: "/about/", sessions: 4526 },
  { page: "/get-free-quote/", sessions: 3768 },
  { page: "/frequently-asked-questions/", sessions: 1875 },
  { page: "/become-a-driver/", sessions: 1310 },
  { page: "/blog/", sessions: 824 },
];

const MOST_VIEWED_PAGES = [
  { page: "/get-free-quote/", views: 14480 },
  { page: "/", views: 10218 },
  { page: "/about/", views: 7657 },
  { page: "/frequently-asked-questions/", views: 5102 },
  { page: "/become-a-driver/", views: 3394 },
  { page: "/blog/", views: 1778 },
];

const APP_STORE_CLICKS = [
  { destination: "Apple App Store", clicks: 252 },
  { destination: "Google Play", clicks: 151 },
];

const ExternalLinkInline = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "inline", marginLeft: 4, color: "#9ca3af", flexShrink: 0 }}
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const WebsiteSection = ({ snapshot }) => {
  const [chartType, setChartType] = useState("line");
  const [selectedMetric, setSelectedMetric] = useState("users-sessions");
  const [selectedInterval, setSelectedInterval] = useState("Daily");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIndicator = (key) =>
    sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕";

  const commonChartProps = {
    data: WEBSITE_CHART_DATA,
    margin: { top: 10, right: 30, left: 0, bottom: 10 },
  };

  const commonAxis = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
      <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: "12px" }} />
      <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
      <Tooltip
        contentStyle={{
          backgroundColor: "#fff",
          border: "1px solid #e5e7eb",
          fontSize: "13px",
        }}
      />
    </>
  );

  const renderChart = () => {
    if (chartType === "area") {
      return (
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart {...commonChartProps}>
            {commonAxis}
            <defs>
              <linearGradient id="colorAU" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorSess" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="activeUsers" stroke="#0d9488" fill="url(#colorAU)" strokeWidth={2} name="Active users" />
            <Area type="monotone" dataKey="sessions" stroke="#3b82f6" fill="url(#colorSess)" strokeWidth={2} name="Sessions" />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === "bar") {
      return (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart {...commonChartProps}>
            {commonAxis}
            <Bar dataKey="activeUsers" fill="#0d9488" name="Active users" radius={[2, 2, 0, 0]} />
            <Bar dataKey="sessions" fill="#3b82f6" name="Sessions" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={320}>
        <LineChart {...commonChartProps}>
          {commonAxis}
          <Line type="monotone" dataKey="activeUsers" stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Active users" />
          <Line type="monotone" dataKey="sessions" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Sessions" />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const kpiData = [
    {
      label: "Active users",
      tooltipTitle: "Distinct active users on the website in the selected period.",
      value: "7,176",
      changePercent: "-4.1%",
      changeType: "negative",
    },
    {
      label: "Sessions",
      tooltipTitle: "Total sessions on the website in the selected period.",
      value: "18,908",
      changePercent: "-5.3%",
      changeType: "negative",
    },
    {
      label: "Engagement rate",
      tooltipTitle: "Percentage of active users who had at least one engaged session.",
      value: "76.9%",
      changePercent: "-0.4%",
      changeType: "negative",
    },
  ];

  return (
    <>
      {/* KPI Cards */}
      <KPICardsGrid cards={kpiData} />

      {/* Website Activity Chart */}
      <div className="analytics-chart-box">
        <div className="analytics-chart-header">
          <div className="analytics-chart-title-header">
            <div className="analytics-chart-title-left">
              <span className="analytics-chart-title">Website activity</span>
              <TooltipIcon
                title="Distinct active users and sessions on the website per interval."
                iconWidth="16"
                iconHeight="16"
                inline={true}
              />
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="analytics-metric-select analytics-metric-select--sm"
              >
                {METRIC_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="analytics-chart-title-right">
              <select
                value={selectedInterval}
                onChange={(e) => setSelectedInterval(e.target.value)}
                className="analytics-interval-select"
              >
                {INTERVAL_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <div className="analytics-chart-type-buttons">
                <button
                  onClick={() => setChartType("bar")}
                  className={`analytics-chart-type-btn ${chartType === "bar" ? "active" : ""}`}
                  title="Bar Chart"
                >
                  <BarGraphIcon1 />
                </button>
                <button
                  onClick={() => setChartType("area")}
                  className={`analytics-chart-type-btn ${chartType === "area" ? "active" : ""}`}
                  title="Area Chart"
                >
                  <BarGraphIcon2 />
                </button>
                <button
                  onClick={() => setChartType("line")}
                  className={`analytics-chart-type-btn ${chartType === "line" ? "active" : ""}`}
                  title="Line Chart"
                >
                  <BarGraphIcon3 />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="analytics-chart-content">
          {renderChart()}
        </div>
        <div className="analytics-chart-legend">
          <span className="analytics-chart-legend__item">
            <span className="analytics-chart-legend__line" style={{ backgroundColor: "#0d9488" }} />
            Active users
          </span>
          <span className="analytics-chart-legend__item">
            <span className="analytics-chart-legend__line" style={{ backgroundColor: "#3b82f6" }} />
            Sessions
          </span>
        </div>
      </div>

      {/* Traffic Channels Table */}
      <div className="analytics-section-table">
        <div className="analytics-section-table__header">
          <h3 className="analytics-section-table__title">Traffic channels</h3>
          <TooltipIcon
            title="Breakdown of website traffic by channel."
            iconWidth="16"
            iconHeight="16"
            inline={true}
          />
        </div>
        <hr className="analytics-section-table__divider" />
        <div className="analytics-section-table__wrapper">
          <table className="analytics-section-table__table">
            <thead>
              <tr className="analytics-section-table__head-row">
                <th
                  className="analytics-section-table__head-cell"
                  onClick={() => handleSort("channel")}
                >
                  Channel {getSortIndicator("channel")}
                </th>
                <th
                  className="analytics-section-table__head-cell analytics-section-table__head-cell--right"
                  onClick={() => handleSort("sessions")}
                >
                  Sessions{" "}
                  <TooltipIcon
                    title="Total sessions from this traffic channel."
                    iconWidth="14"
                    iconHeight="14"
                    inline={true}
                  />{" "}
                  {getSortIndicator("sessions")}
                </th>
                <th
                  className="analytics-section-table__head-cell analytics-section-table__head-cell--right"
                  onClick={() => handleSort("shareOfSessions")}
                >
                  Share of sessions{" "}
                  <TooltipIcon
                    title="Percentage of total sessions from this channel."
                    iconWidth="14"
                    iconHeight="14"
                    inline={true}
                  />{" "}
                  {getSortIndicator("shareOfSessions")}
                </th>
                <th
                  className="analytics-section-table__head-cell analytics-section-table__head-cell--right"
                  onClick={() => handleSort("engagementRate")}
                >
                  Engagement rate{" "}
                  <TooltipIcon
                    title="Percentage of sessions from this channel that were engaged."
                    iconWidth="14"
                    iconHeight="14"
                    inline={true}
                  />{" "}
                  {getSortIndicator("engagementRate")}
                </th>
              </tr>
            </thead>
            <tbody>
              {TRAFFIC_CHANNELS.map((row, idx) => (
                <tr key={idx} className="analytics-section-table__body-row">
                  <td className="analytics-section-table__body-cell">{row.channel}</td>
                  <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                    {row.sessions.toLocaleString()}
                  </td>
                  <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                    {row.shareOfSessions}
                  </td>
                  <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                    {row.engagementRate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Landing Pages + Most Viewed Pages */}
      <div className="analytics-tables-two-col">
        {/* Landing Pages */}
        <div className="analytics-section-table">
          <div className="analytics-section-table__header">
            <h3 className="analytics-section-table__title">Landing pages</h3>
            <TooltipIcon
              title="Pages where users entered your website."
              iconWidth="16"
              iconHeight="16"
              inline={true}
            />
          </div>
          <hr className="analytics-section-table__divider" />
          <div className="analytics-section-table__wrapper">
            <table className="analytics-section-table__table">
              <thead>
                <tr className="analytics-section-table__head-row">
                  <th className="analytics-section-table__head-cell">Page</th>
                  <th className="analytics-section-table__head-cell analytics-section-table__head-cell--right">
                    Sessions{" "}
                    <TooltipIcon
                      title="Number of sessions that started on this page."
                      iconWidth="14"
                      iconHeight="14"
                      inline={true}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {LANDING_PAGES.map((row, idx) => (
                  <tr key={idx} className="analytics-section-table__body-row">
                    <td className="analytics-section-table__body-cell">
                      <span className="analytics-section-table__page-link">
                        {row.page}
                        <ExternalLinkInline />
                      </span>
                    </td>
                    <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                      {row.sessions.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Most Viewed Pages */}
        <div className="analytics-section-table">
          <div className="analytics-section-table__header">
            <h3 className="analytics-section-table__title">Most viewed pages</h3>
            <TooltipIcon
              title="Pages with the highest number of views."
              iconWidth="16"
              iconHeight="16"
              inline={true}
            />
          </div>
          <hr className="analytics-section-table__divider" />
          <div className="analytics-section-table__wrapper">
            <table className="analytics-section-table__table">
              <thead>
                <tr className="analytics-section-table__head-row">
                  <th className="analytics-section-table__head-cell">Page</th>
                  <th className="analytics-section-table__head-cell analytics-section-table__head-cell--right">
                    Views{" "}
                    <TooltipIcon
                      title="Total number of times this page was viewed."
                      iconWidth="14"
                      iconHeight="14"
                      inline={true}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {MOST_VIEWED_PAGES.map((row, idx) => (
                  <tr key={idx} className="analytics-section-table__body-row">
                    <td className="analytics-section-table__body-cell">
                      <span className="analytics-section-table__page-link">
                        {row.page}
                        <ExternalLinkInline />
                      </span>
                    </td>
                    <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                      {row.views.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Website Clicks to App Stores */}
      <div className="analytics-section-table">
        <div className="analytics-section-table__header">
          <h3 className="analytics-section-table__title">Website clicks to app stores</h3>
          <TooltipIcon
            title="Clicks from website to mobile app store links."
            iconWidth="16"
            iconHeight="16"
            inline={true}
          />
        </div>
        <hr className="analytics-section-table__divider" />
        <div className="analytics-section-table__wrapper">
          <table className="analytics-section-table__table">
            <thead>
              <tr className="analytics-section-table__head-row">
                <th className="analytics-section-table__head-cell">Destination</th>
                <th className="analytics-section-table__head-cell analytics-section-table__head-cell--right">
                  Clicks{" "}
                  <TooltipIcon
                    title="Number of clicks to this app store."
                    iconWidth="14"
                    iconHeight="14"
                    inline={true}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {APP_STORE_CLICKS.map((row, idx) => (
                <tr key={idx} className="analytics-section-table__body-row">
                  <td className="analytics-section-table__body-cell">{row.destination}</td>
                  <td className="analytics-section-table__body-cell analytics-section-table__body-cell--right">
                    {row.clicks.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default WebsiteSection;
